// Decide, before any frame is drawn, whether this device gets the live ember
// scene and at which tier, or the still poster. `decide` is pure, so every
// rule is unit-tested; `readDevice` gathers the inputs in a browser. The
// governor (governor.ts) corrects the choice from measured frame times.

import type { TierName } from './config'

export interface DeviceInfo {
  renderer: string
  reducedMotion: boolean
  saveData: boolean
  effectiveType?: string
  deviceMemory?: number
  cores?: number
  mobile: boolean
  maxTextureSize: number
  webgl2: boolean
  /** EXT_color_buffer_float: HDR render targets for the bloom. */
  floatRender: boolean
  /** WEBGL_compressed_texture_astc: on a Mac, only Apple silicon has it. */
  astc: boolean
  /** `?ember=high|medium|low|poster`, for tests and support. */
  forced?: string | null
}

export type Decision =
  | { mode: 'live'; tier: TierName; reason: string }
  | { mode: 'poster'; reason: string }

const SOFTWARE = /swiftshader|llvmpipe|softpipe|lavapipe|software|basic render|microsoft basic|mesa offscreen/i
const RANK: Record<TierName, number> = { low: 0, medium: 1, high: 2 }

function gpuClass(renderer: string, mobile: boolean, astc: boolean): { tier: TierName; reason: string } {
  const r = renderer.toLowerCase()
  if (/apple m\d/.test(r)) return { tier: 'high', reason: 'apple-silicon' }
  if (/apple gpu|apple a\d{2}/.test(r)) {
    if (mobile) return { tier: 'medium', reason: 'apple-mobile' }
    // Safari names every Mac GPU "Apple GPU", old Intel and AMD parts too.
    return astc ? { tier: 'high', reason: 'apple-gpu' } : { tier: 'medium', reason: 'apple-gpu-intel-era' }
  }
  if (/nvidia|geforce|rtx|quadro/.test(r)) {
    if (/\bgt \d{3,4}m?\b|\bmx\s?\d{2,3}\b|gtx (6|7|9)\d\dm?\b/.test(r)) return { tier: 'medium', reason: 'nvidia-older' }
    return { tier: 'high', reason: 'nvidia' }
  }
  if (/radeon/.test(r)) {
    if (/vega (3|6|8)\b|r[2-7] |hd \d{4}|radeon(\(tm\))? (graphics|vega)/.test(r)) return { tier: 'medium', reason: 'radeon-integrated' }
    return { tier: 'high', reason: 'radeon' }
  }
  if (/intel/.test(r)) {
    if (/arc|iris\(r\) xe|iris xe/.test(r)) return { tier: 'high', reason: 'intel-xe' }
    // HD 2000-6000 (2011-2015) and the Atom-era HD 400s are the old parts.
    const hd = r.match(/hd graphics (\d{3,4})/)
    if (hd && (Number(hd[1]) < 500 || Number(hd[1]) >= 1000)) return { tier: 'low', reason: 'intel-hd-old' }
    return { tier: 'medium', reason: 'intel' }
  }
  const adreno = r.match(/adreno[^\d]*(\d{3})/)
  if (adreno) return Number(adreno[1]) >= 610 ? { tier: 'medium', reason: 'adreno' } : { tier: 'low', reason: 'adreno-old' }
  const mali = r.match(/mali-g(\d{2,3})/)
  if (mali) return { tier: Number(mali[1]) >= 76 ? 'medium' : 'low', reason: 'mali' }
  if (/mali-t|powervr|videocore|vivante/.test(r)) return { tier: 'low', reason: 'legacy-mobile-gpu' }
  if (/immortalis|xclipse/.test(r)) return { tier: 'medium', reason: 'mobile-gpu' }
  return { tier: 'medium', reason: 'unknown-gpu' }
}

export function decide(info: DeviceInfo): Decision {
  const forced = info.forced?.toLowerCase()
  if (forced === 'poster') return { mode: 'poster', reason: 'forced' }
  if (forced === 'high' || forced === 'medium' || forced === 'low') {
    return info.webgl2 ? { mode: 'live', tier: forced, reason: 'forced' } : { mode: 'poster', reason: 'no-webgl2' }
  }

  if (info.reducedMotion) return { mode: 'poster', reason: 'reduced-motion' }
  if (info.saveData) return { mode: 'poster', reason: 'save-data' }
  if (info.effectiveType === 'slow-2g' || info.effectiveType === '2g') return { mode: 'poster', reason: 'slow-network' }
  if (!info.webgl2) return { mode: 'poster', reason: 'no-webgl2' }
  if (!info.floatRender) return { mode: 'poster', reason: 'no-float-render' }
  if (SOFTWARE.test(info.renderer)) return { mode: 'poster', reason: 'software-renderer' }
  if (info.maxTextureSize < 2048) return { mode: 'poster', reason: 'small-textures' }

  let { tier, reason } = gpuClass(info.renderer, info.mobile, info.astc)
  const cap = (max: TierName, why: string) => {
    if (RANK[tier] > RANK[max]) {
      tier = max
      reason += `+${why}`
    }
  }
  if (info.mobile) cap('medium', 'mobile')
  if (info.maxTextureSize < 4096) cap('low', 'textures')
  if (info.deviceMemory !== undefined) {
    if (info.deviceMemory <= 2) cap('low', 'memory')
    else if (info.deviceMemory <= 4) cap('medium', 'memory')
  }
  if (info.cores !== undefined) {
    if (info.cores <= 2) cap('low', 'cores')
    else if (info.cores <= 4) cap('medium', 'cores')
  }
  if (info.effectiveType === '3g') cap('medium', '3g')
  return { mode: 'live', tier, reason }
}

interface NavigatorHints {
  connection?: { saveData?: boolean; effectiveType?: string }
  deviceMemory?: number
  userAgentData?: { mobile?: boolean }
}

/**
 * Reads the device in a browser. The WebGL2 probe uses a throwaway canvas and
 * releases its context at once, so the live scene's own context is the only
 * one that stays.
 */
export function readDevice(search = typeof location !== 'undefined' ? location.search : ''): DeviceInfo {
  const nav = (typeof navigator !== 'undefined' ? navigator : {}) as Navigator & NavigatorHints
  const media = (query: string) => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches
  const info: DeviceInfo = {
    renderer: '',
    reducedMotion: media('(prefers-reduced-motion: reduce)'),
    saveData: Boolean(nav.connection?.saveData),
    effectiveType: nav.connection?.effectiveType,
    deviceMemory: nav.deviceMemory,
    cores: nav.hardwareConcurrency,
    mobile: Boolean(nav.userAgentData?.mobile) || /iphone|ipad|ipod|android|mobile/i.test(nav.userAgent ?? '') || (media('(pointer: coarse)') && media('(max-width: 900px)')),
    maxTextureSize: 0,
    webgl2: false,
    floatRender: false,
    astc: false,
    forced: new URLSearchParams(search).get('ember'),
  }
  if (typeof WebGL2RenderingContext === 'undefined' || typeof document === 'undefined') return info
  const canvas = document.createElement('canvas')
  const gl = canvas.getContext('webgl2', { failIfMajorPerformanceCaveat: false })
  if (!gl) return info
  info.webgl2 = true
  const debug = gl.getExtension('WEBGL_debug_renderer_info')
  info.renderer = String(debug ? gl.getParameter(debug.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER))
  info.maxTextureSize = Number(gl.getParameter(gl.MAX_TEXTURE_SIZE)) || 0
  info.floatRender = Boolean(gl.getExtension('EXT_color_buffer_float'))
  info.astc = Boolean(gl.getExtension('WEBGL_compressed_texture_astc'))
  gl.getExtension('WEBGL_lose_context')?.loseContext()
  return info
}
